import React from "react";
import { useWatch } from "react-hook-form";
import { Text } from "@chakra-ui/react";
import { GroupedCheckboxButtonGroup } from "./CheckboxButtonGroup.jsx";
import { getFilteredSiteIds } from "./SelectedSitesInput.filters.js";

export { getFilteredSiteIds };

export function useSelectedSites({ facility, formContext }) {
  const { control } = formContext;
  const selectedSiteIds = useWatch({ name: "selectedSiteIds", control });

  const memo = {};

  for (let i = 0; i < selectedSiteIds.length; i++) {
    memo[selectedSiteIds[i]] = true;
  }

  return facility.sites.filter((site) => memo[site.id]);
}

export function useFilteredSites({ facility, formContext }) {
  const {
    setValue,
    formState: { isDirty },
    watch,
  } = formContext;

  const [
    siteType,
    maxRVLength,
    selectedFilters,
    doubleSitePreference,
    accessibilityPreference,
    maxElectric,
  ] = watch([
    "siteType",
    "maxRVLength",
    "selectedFilters",
    "doubleSitePreference",
    "accessibilityPreference",
    "maxElectric",
  ]);

  const filteredSiteIds = React.useMemo(() => {
    return getFilteredSiteIds({
      facility,
      siteType,
      maxRVLength,
      selectedFilters,
      doubleSitePreference,
      accessibilityPreference,
      maxElectric,
    });
  }, [
    siteType,
    maxRVLength,
    selectedFilters,
    doubleSitePreference,
    accessibilityPreference,
    maxElectric,
  ]);

  React.useEffect(() => {
    if (isDirty) {
      setValue("selectedSiteIds", filteredSiteIds, { shouldValidate: true });
    }
  }, [filteredSiteIds.join(",")]); // the .join is important to make this equality check work

  const filteredSites = React.useMemo(() => {
    const isFilteredSite = Object.fromEntries(
      filteredSiteIds.map((siteId) => [siteId, true]),
    );

    const collator = new Intl.Collator("en", {
      numeric: true,
      sensitivity: "base",
    });

    // Sort function that handles time-based site names (e.g., "10:30am", "2:00pm")
    const sortSites = (a, b) => {
      // For Melanzana time slots, extract time and convert to 24h for sorting
      const timeRegex = /^(\d{1,2}):(\d{2})(am|pm)$/;
      const matchA = a.label.match(timeRegex);
      const matchB = b.label.match(timeRegex);

      if (matchA && matchB) {
        // Both are time slots - parse and compare
        let hourA = parseInt(matchA[1], 10);
        let hourB = parseInt(matchB[1], 10);
        const minA = parseInt(matchA[2], 10);
        const minB = parseInt(matchB[2], 10);

        // Convert to 24h format
        if (matchA[3] === "pm" && hourA !== 12) hourA += 12;
        if (matchA[3] === "am" && hourA === 12) hourA = 0;
        if (matchB[3] === "pm" && hourB !== 12) hourB += 12;
        if (matchB[3] === "am" && hourB === 12) hourB = 0;

        // Compare by hour, then by minute
        if (hourA !== hourB) return hourA - hourB;
        return minA - minB;
      }

      // Fall back to natural sort for non-time sites
      return collator.compare(a.label, b.label);
    };

    return facility.sites
      .filter((site) => isFilteredSite[site.id])
      .map((site) => ({
        value: `${site.id}`,
        label: site.name,
        group: site.group,
      }))
      .sort(sortSites);
  }, [filteredSiteIds.join(",")]);

  return filteredSites;
}

export function SiteCountText({ formContext, ...rest }) {
  const { control } = formContext;

  const selectedSiteIds = useWatch({ name: "selectedSiteIds", control });
  const siteCount = selectedSiteIds.length;

  return (
    <Text color={siteCount === 0 ? "error" : "accent"} {...rest}>
      ({siteCount} selected)
    </Text>
  );
}

export function SelectedSitesInput({ name, facility, formContext, ...rest }) {
  const filteredSites = useFilteredSites({ facility, formContext });

  const { control } = formContext;

  return (
    <GroupedCheckboxButtonGroup
      isRequired={true}
      isRequiredMessage="No sites selected. Please review your site selection"
      control={control}
      name={name}
      label={
        <>
          <Text as="span" whiteSpace="nowrap">
            Select from matching sites&nbsp;
          </Text>
          <SiteCountText
            as="span"
            whiteSpace="nowrap"
            formContext={formContext}
            fontWeight="bold"
          />
        </>
      }
      options={filteredSites}
      size="sm"
      groupBy={(site) => site.group || "All Sites"}
      {...rest}
    />
  );
}
